import * as React from 'react'

import { Trash2 } from 'lucide-react'
import { observer } from 'mobx-react'

import ColumnsConfigurationMenu from '~/client/src/desktop/components/HeaderBar/components/ColumnsConfigurationMenu'
import { CollapseButton } from '~/client/src/shared/components/CollapseButton/CollapseButton'
import ConfirmDialog from '~/client/src/shared/components/ConfirmDialog/ConfirmDialog'
import { DisabledReasonTooltip } from '~/client/src/shared/components/DisabledReasonTooltip'
import { SettingsIcon } from '~/client/src/shared/components/icons/SettingsIcon'
import MenuCloser from '~/client/src/shared/components/MenuCloser'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import {
  IViewOptionsPanelProps,
  ViewOptionsPopover,
} from '~/client/src/shared/components/ViewOptions'
import SortOrder from '~/client/src/shared/enums/SortOrder'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { NOOP } from '~/client/src/shared/utils/noop'

import { useResource } from '../context'
import { useSelectionHasLockedRecord } from '../hooks/useSelectionHasLockedRecord'
import { useSelectionHasProjectLocation } from '../hooks/useSelectionHasProjectLocation'

export const ResourceCollapseToggle: React.FC = () => {
  const {
    state: { isAllCollapsed },
    actions: { collapseAll, expandAll },
  } = useResource()
  const isAllExpanded = !isAllCollapsed

  return (
    <div className="tw:flex tw:items-center tw:grow-0">
      <CollapseButton
        isExpanded={isAllExpanded}
        onClick={isAllCollapsed ? expandAll : collapseAll}
        ariaLabel={
          isAllExpanded
            ? Localization.translator.collapseAll
            : Localization.translator.expandAll
        }
      />
    </div>
  )
}

interface ResourceRecordCountProps {
  label?: (count: number) => string
}

export const ResourceRecordCount: React.FC<ResourceRecordCountProps> = ({
  label = (c: number) => Localization.translator.xEquipment(c),
}) => {
  const {
    meta: { filteredCount, selectedCount },
  } = useResource()

  return (
    <div
      className="tw:flex tw:items-center tw:grow-0 tw:whitespace-nowrap tw:text-label-lg tw:text-muted-foreground"
      data-testid="resource-record-count"
    >
      {selectedCount > 0
        ? Localization.translator.xOfYSelected(selectedCount, filteredCount)
        : label(filteredCount)}
    </div>
  )
}

interface ResourceDeleteButtonProps {
  confirmationLabel?: (count: number) => string
}

export const ResourceDeleteButton: React.FC<ResourceDeleteButtonProps> =
  observer(
    ({
      confirmationLabel = (c: number) =>
        Localization.translator.confirmDeletionOfEquipment(c),
    }) => {
      const {
        meta: { selectedCount },
        actions: { deleteSelected },
      } = useResource()

      const hasProjectLocationSelected = useSelectionHasProjectLocation()
      const hasLockedRecordSelected = useSelectionHasLockedRecord()

      const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false)
      const [isDeleting, setIsDeleting] = React.useState(false)

      const openDeleteDialog = React.useCallback(() => {
        setIsDeleteDialogOpen(true)
      }, [])

      const closeDeleteDialog = React.useCallback(() => {
        setIsDeleteDialogOpen(false)
      }, [])

      const handleDelete = React.useCallback(async () => {
        setIsDeleting(true)
        try {
          await deleteSelected()
          setIsDeleteDialogOpen(false)
        } catch (error) {
          console.error('Failed to delete:', error)
        } finally {
          setIsDeleting(false)
        }
      }, [deleteSelected])

      if (selectedCount === 0) return null

      return (
        <>
          <div className="tw:border-0 tw:border-l tw:border-solid tw:border-border tw:h-7 tw:grow-0" />
          <DisabledReasonTooltip
            isDenied={hasProjectLocationSelected || hasLockedRecordSelected}
            reason={
              hasProjectLocationSelected
                ? Localization.translator.projectLocationDeletionProhibited
                : Localization.translator.selectionHasResourcesYouCannotChange
            }
          >
            <Button
              variant="destructive-ghost"
              disabled={hasProjectLocationSelected || hasLockedRecordSelected}
              onClick={openDeleteDialog}
              data-testid="resource-delete-button"
            >
              <Trash2 />
              {Localization.translator.delete}
            </Button>
          </DisabledReasonTooltip>
          <ConfirmDialog
            useRedConfirmationButton
            title={Localization.translator.deleteSelected}
            isOpen={isDeleteDialogOpen}
            loading={isDeleting}
            onCancelClicked={isDeleting ? NOOP : closeDeleteDialog}
            onDoneClicked={handleDelete}
            doneTitle={Localization.translator.delete}
          >
            <div className="tw:text-body-md tw:whitespace-pre-line tw:pb-2.5">
              {confirmationLabel(selectedCount)}
            </div>
          </ConfirmDialog>
        </>
      )
    },
  )

export const ResourceDisplayOptions: React.FC = () => {
  const {
    groupSortProps,
    configuredColumns,
    defaultColumns,
    updateColumnsConfiguration,
    resetColumnsConfiguration,
    excludedFromSortKeys,
  } = useResource().meta

  const viewOptionsProps = React.useMemo<IViewOptionsPanelProps>(
    () => ({
      grouping: groupSortProps
        ? {
            value: groupSortProps.selectedGroupByKey,
            options: groupSortProps.groupingOptions || [],
            defaultValue: groupSortProps.initialGroupByKey,
            onChange: groupSortProps.onGroupingChange,
          }
        : undefined,
      sorting: groupSortProps
        ? {
            value: {
              key: groupSortProps.selectedSortingOptionId || null,
              order: groupSortProps.selectedSortOrder,
            },
            defaultValue: {
              key: groupSortProps.initialSortKey,
              order: groupSortProps.initialSortOrder,
            },
            options: groupSortProps.sortingOptions || [],
            onChange: value =>
              groupSortProps.onSortingChange(
                value.key ?? '',
                value.order ?? SortOrder.DEFAULT,
              ),
          }
        : undefined,
      columns: configuredColumns?.length
        ? {
            items: configuredColumns
              .filter(column => !excludedFromSortKeys.includes(column.dataKey))
              .map(column => ({
                key: column.dataKey,
                label: column.label || column.dataKey,
                isFixed: column.isFixed,
                isHidden: column.isHidden,
              })),
            defaultItems: defaultColumns
              .filter(column => !excludedFromSortKeys.includes(column.dataKey))
              .map(column => ({
                key: column.dataKey,
                label: column.label || column.dataKey,
                isFixed: column.isFixed,
                isHidden: column.isHidden,
              })),
            onChange: items =>
              updateColumnsConfiguration(
                items.map(item => ({
                  dataKey: item.key,
                  isFixed: !!item.isFixed,
                  isHidden: !!item.isHidden,
                })),
              ),
            onReset: resetColumnsConfiguration,
          }
        : undefined,
    }),
    [
      configuredColumns,
      defaultColumns,
      excludedFromSortKeys,
      groupSortProps,
      resetColumnsConfiguration,
      updateColumnsConfiguration,
    ],
  )

  if (
    !viewOptionsProps.grouping &&
    !viewOptionsProps.sorting &&
    !viewOptionsProps.columns
  ) {
    return null
  }

  return (
    <div className="tw:flex tw:items-center tw:grow-0">
      <ViewOptionsPopover {...viewOptionsProps} />
    </div>
  )
}

export const ResourceColumnConfig: React.FC = () => {
  const {
    meta: {
      configuredColumns,
      updateColumnsConfiguration,
      resetColumnsConfiguration,
      excludedFromSortKeys,
    },
  } = useResource()

  const [isMenuShown, setIsMenuShown] = React.useState(false)
  const [searchKey, setSearchKey] = React.useState('')

  const toggleMenu = React.useCallback(() => {
    setIsMenuShown(prev => !prev)
  }, [])

  const changeSearchKey = React.useCallback((newSearchKey: string) => {
    setSearchKey(newSearchKey)
  }, [])

  if (!configuredColumns?.length) return null

  return (
    <div className="tw:flex tw:items-center tw:flex-1 tw:justify-end">
      {isMenuShown && (
        <MenuCloser
          closeMenu={toggleMenu}
          className="tw:flex tw:items-center tw:justify-end"
        >
          <ColumnsConfigurationMenu
            columns={configuredColumns}
            restoreDefaults={resetColumnsConfiguration}
            searchKey={searchKey}
            changeSearchKey={changeSearchKey}
            updateColumnsConfiguration={updateColumnsConfiguration}
            excludedFromSort={excludedFromSortKeys}
            isLoading={false}
          />
        </MenuCloser>
      )}
      <div
        className="tw:flex tw:items-center tw:cursor-pointer tw:grow-0 tw:text-muted-foreground"
        onClick={toggleMenu}
      >
        <SettingsIcon
          size="sm"
          className={
            isMenuShown
              ? 'tw:mx-2.5 tw:rounded-full tw:bg-primary/10'
              : 'tw:mx-2.5 tw:rounded-full tw:hover:bg-muted'
          }
        />
      </div>
    </div>
  )
}
